type ErrorWithMessage = {
  message: string;
};

interface ErrorModel {
  exception: null;
  errorMessage: string;
}

interface ValidationError {
  [key: string]: {
    errors: ErrorModel[];
  };
}

interface BadRequestError {
  [key: string]: string;
}

interface ErrorAndErrorCode {
  Error: string;
  ErrorCode: string;
}

const isErrorAndErrorCode = (error: unknown): error is ErrorAndErrorCode => {
  const isErrorWithMessage =
    typeof error === 'object' &&
    error !== null &&
    'Error' in error &&
    'ErrorCode' in error &&
    typeof (error as ErrorAndErrorCode).Error === 'string' &&
    typeof (error as ErrorAndErrorCode).ErrorCode === 'string';

  return isErrorWithMessage;
};

const isErrorWithMessage = (error: unknown): error is ErrorWithMessage => {
  const isErrorWithMessage =
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string';

  return isErrorWithMessage;
};

const isValidationError = (error: unknown): error is ValidationError => {
  const isValidationError =
    typeof error !== 'string' &&
    typeof error === 'object' &&
    error !== null &&
    Array.isArray(
      (error as ValidationError)[Object.getOwnPropertyNames(error)[0]].errors
    );

  return isValidationError;
};

const isBadRequest = (error: unknown): error is BadRequestError => {
  const isBadRequest =
    typeof error !== 'string' &&
    typeof error === 'object' &&
    error !== null &&
    Array.isArray(
      (error as BadRequestError)[Object.getOwnPropertyNames(error)[0]]
    );

  return isBadRequest;
};

const getValidationError = (error: ValidationError): string => {
  return Object.getOwnPropertyNames(error)
    .map((propName: string) => {
      return error[propName].errors[0].errorMessage;
    })
    .join(' ');
};

const getBadRequestError = (error: BadRequestError): string => {
  return Object.getOwnPropertyNames(error)
    .map((propName: string) => {
      return error[propName];
    })
    .join(' ');
};

const toErrorWithMessage = (maybeError: unknown): ErrorWithMessage => {
  if (isValidationError(maybeError)) {
    return new Error(getValidationError(maybeError));
  }

  if (isBadRequest(maybeError)) {
    return new Error(getBadRequestError(maybeError));
  }

  if (isErrorWithMessage(maybeError)) {
    return maybeError;
  }

  if (isErrorAndErrorCode(maybeError)) {
    return new Error(
      `${maybeError.ErrorCode || 'Unknown Error'}: ${maybeError.Error}`
    );
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
};

export const getErrorMessage = (error: unknown) => {
  return toErrorWithMessage(error).message;
};
